import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { DataRequestsState } from '@/types/dataRequestsState';
import { EditableField, EntityName } from '@/types/editableField';
import type { RawEntityPropertyDefinition } from '@/types/eds/RawDefinitions';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { AssignmentAssigneeListItem, AssignmentQueueListItem } from '@/types/entities/assignmentQueueItem';
import type { CauseOfIncidentListItem } from '@/types/entities/causeOfIncidentListItem';
import type { UserSelectListItem } from '@/types/entities/userSelectListItem';
import type { PageList } from '@/types/list';
import type { PaginatedListNormalizedResponse } from '@/types/pagination';
import type { StateSelectOption } from '@/types/stateSelectOption';
import type { GetClaimSetClaimUpdateOptionsResponse } from '@/features/editMode/types/getClaimSetClaimUpdateOptions';
import type { GetClaimSetUpdateOptionsResponse } from '@/features/editMode/types/getClaimSetUpdateOptions';
import type { SelectOption } from '@/features/editMode/types/selectOption';
import type { NormalizedValidationResult } from '@/features/eds/types/normalizedValidationResult';
import type { ContactPreferenceUpdateOptions } from '../types/contactPreferenceUpdateOptions';
import type { GetClaimSetTaskUpdateOptionsResponse } from '../types/getClaimSetTaskUpdateOptions';
import type { GetClaimantUpdateOptionsResponse } from '../types/getClaimantUpdateOptions';
import type { GetPolicyUpdateOptionsResponse } from '../types/getPolicyUpdateOptions';
import type { GetProfileUpdateOptionsResponse } from '../types/getProfileUpdateOptionsResponse';
import { EditModeState } from '@/features/editMode/enums/EditModeState';
import { EditModeStatus } from '@/features/editMode/enums/EditModeStatus';

export type EditModeSliceState = {
  status: EditModeStatus;
  state: EditModeState;
  isInEditMode: boolean;
  editedFields: EditableField[];

  claimSetUpdateOptions: GetClaimSetUpdateOptionsResponse;
  claimSetClaimUpdateOptions: GetClaimSetClaimUpdateOptionsResponse;
  claimSetTaskUpdateOptions: GetClaimSetTaskUpdateOptionsResponse;
  claimantUpdateOptions: GetClaimantUpdateOptionsResponse;
  contactPreferenceUpdateOptions: ContactPreferenceUpdateOptions;
  policyUpdateOptions: GetPolicyUpdateOptionsResponse;

  profileUpdateOptions: GetProfileUpdateOptionsResponse;

  reportingUserListData: PaginatedListNormalizedResponse<UserSelectListItem> | null;
  reportingUserListPagination: PageList & { searchExpression: string };

  stateListData: StateSelectOption[];
  stateListPagination: { searchExpression: string };

  causeOfIncidentListData: CauseOfIncidentListItem[];
  causeOfIncidentListPagination: { searchExpression: string };

  documentDeliveryPreferencesUpdateOptions: SelectOption[];
  genderUpdateOptions: SelectOption[];

  dataRequestsStates: {
    reportingUserListData: DataRequestsState;
    stateListData: DataRequestsState;
    causeOfIncidentListData: DataRequestsState;
    assignmentListData: DataRequestsState;
  };

  normalizedValidationResultList: NormalizedValidationResult[];
  isValidationInProgress: boolean;
  blocking: {
    isEnabled: boolean;
    except?: { entityName: string; fieldName: string }[];
    only?: { entityName: string; fieldName: string }[];
  };
  shouldCloseEditModeOnError: boolean;
  assigneeByPersonId: Record<string, EditModeAssignee>;

  assignmentListData: AssignmentQueueListItem[] | null;
  assigneeListDataByQueueId: Record<string, AssignmentAssigneeListItem[]>;
  assignmentListPagination: { searchExpression: string };
};

export type EditModeAssignee = { data?: NormalizedV2Response; status?: 'pending' | 'loading' | 'loaded' };

const initialState: EditModeSliceState = {
  status: EditModeStatus.Enabled,
  state: EditModeState.Disabled,
  isInEditMode: false,
  editedFields: [],
  reportingUserListData: null,
  reportingUserListPagination: {
    page: 1,
    perPage: 20,
    searchExpression: '',
  },
  causeOfIncidentListData: [],
  causeOfIncidentListPagination: {
    searchExpression: '',
  },
  claimSetUpdateOptions: {
    statuses: [],
    linesOfBusiness: [],
    reportingMethods: [],
    reportingUsers: [],
    foreignDeathOptions: [],
    dateOfDeathEstimations: [],
    mannersOfDeath: [],
    claimTypes: [],
  },
  claimSetClaimUpdateOptions: {
    statuses: [],
  },
  claimSetTaskUpdateOptions: {
    statuses: [],
    priorities: [],
  },
  claimantUpdateOptions: {
    statuses: [],
  },
  contactPreferenceUpdateOptions: [],
  policyUpdateOptions: {
    policySources: [],
    policyStatuses: [],
    linesOfBusiness: [],
    productTypes: [],
    policyTypes: [],
    matchTypes: [],
    variablePolicy: [],
  },
  profileUpdateOptions: {
    notificationChannels: [],
    otpChannels: [],
  },
  documentDeliveryPreferencesUpdateOptions: [],
  genderUpdateOptions: [],
  stateListData: [],
  stateListPagination: {
    searchExpression: '',
  },
  dataRequestsStates: {
    reportingUserListData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    stateListData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    causeOfIncidentListData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    assignmentListData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
  },
  normalizedValidationResultList: [],
  isValidationInProgress: false,
  blocking: { isEnabled: false },
  shouldCloseEditModeOnError: true,
  assigneeByPersonId: {},

  assignmentListData: null,
  assignmentListPagination: { searchExpression: '' },
  assigneeListDataByQueueId: {},
};

const addEditedFieldHandler = (state, { payload }: PayloadAction<EditableField>) => {
  const existingField = state.editedFields.find(
    (field) =>
      field.entityId === payload.entityId &&
      field.entityField === payload.entityField &&
      field.entityName === payload.entityName,
  );

  // the main check is in the useEdsField hook, but in the status reason modals we do not use useEdsField hook
  if (payload.oldValue === payload.value) {
    return;
  }

  if (existingField) {
    if (payload.value === existingField.value) {
      return;
    }

    existingField.value = payload.value;
    return;
  }

  state.editedFields.push(payload);
};

const editModeSlice = createSlice({
  name: 'editMode',
  initialState,
  reducers: {
    validateFields(state) {
      state.isValidationInProgress = true;
    },
    validateFieldsSuccess(state, { payload }: PayloadAction<NormalizedValidationResult[]>) {
      state.normalizedValidationResultList = payload;
      state.isValidationInProgress = false;
    },
    validateFieldsError(state) {
      state.isValidationInProgress = false;
    },
    resetEditMode() {
      return initialState;
    },
    saveEditModeData: () => {},
    getClaimSetUpdateOptions: () => {},
    getClaimSetClaimUpdateOptions: () => {},
    getClaimSetTaskUpdateOptions: () => {},
    getClaimantUpdateOptions: () => {},
    getBeneficiaryContactPreferenceUpdateOptions: () => {},
    getDocumentDeliveryPreferencesUpdateOptions: () => {},
    getGenderUpdateOptions: () => {},
    getPolicyUpdateOptions: () => {},
    getProfileUpdateOptions: () => {},
    changeEditModeStatus(state, { payload }: PayloadAction<EditModeStatus>) {
      if (payload === EditModeStatus.Blocked) {
        return {
          ...state,
          status: payload,
          isInEditMode: false,
          state: EditModeState.Disabled,
          editedFields: [],
          normalizedValidationResultList: [],
        };
      }

      return {
        ...state,
        status: payload,
      };
    },
    setEditModeBehaviorOnError(
      state,
      {
        payload,
      }: PayloadAction<{
        shouldCloseEditModeOnError?: boolean;
      }>,
    ) {
      if ('shouldCloseEditModeOnError' in payload)
        state.shouldCloseEditModeOnError = Boolean(payload.shouldCloseEditModeOnError);
    },
    changeEditModeState(state, { payload }: PayloadAction<EditModeState>) {
      if (payload === EditModeState.Canceled || payload === EditModeState.Saved) {
        return {
          ...state,
          isInEditMode: false,
          state: payload,
          editedFields: [],
          normalizedValidationResultList: [],
        };
      }

      if (payload === EditModeState.Enabled) {
        return { ...state, state: payload, isInEditMode: true, shouldCloseEditModeOnError: true };
      }

      state.state = payload;
    },
    addEditedField: addEditedFieldHandler,
    addCustomEditedField: addEditedFieldHandler,
    removeEditedField(state, { payload }: PayloadAction<EditableField>) {
      state.editedFields = state.editedFields.filter(
        (field) =>
          field.entityField !== payload.entityField &&
          field.entityId !== payload.entityId &&
          field.entityName !== payload.entityName,
      );
    },
    removeHiddenEditedFields(
      state,
      {
        payload,
      }: PayloadAction<{ entityId: number; entityName: EntityName; property: Partial<RawEntityPropertyDefinition> }[]>,
    ) {
      payload.forEach((override) => {
        state.editedFields = state.editedFields.filter((editedField) => {
          if (!override.property?.propertyName) return true;
          if (override.property?.hidden) return true;

          return (
            editedField.entityField !== override.property?.propertyName &&
            editedField.entityId !== override.entityId &&
            editedField.entityName !== override.entityName
          );
        });
      });
    },
    resetEditedField(state, { payload }: PayloadAction<EditableField>) {
      state.editedFields = state.editedFields.reduce((fields, field) => {
        if (
          field.entityField === payload.entityField &&
          field.entityId === payload.entityId &&
          field.entityName === payload.entityName
        ) {
          return [...fields, { ...field, value: payload.value, oldValue: payload.oldValue }];
        }

        return [...fields, field];
      }, [] as EditableField[]);
    },
    // UPDATE OPTIONS
    getClaimSetUpdateOptionsSuccess(state, { payload }: PayloadAction<GetClaimSetUpdateOptionsResponse>) {
      state.claimSetUpdateOptions = payload;
    },
    getClaimSetClaimUpdateOptionsSuccess(state, { payload }: PayloadAction<GetClaimSetClaimUpdateOptionsResponse>) {
      state.claimSetClaimUpdateOptions = payload;
    },
    getClaimSetTaskUpdateOptionsSuccess(state, { payload }: PayloadAction<GetClaimSetTaskUpdateOptionsResponse>) {
      state.claimSetTaskUpdateOptions = payload;
    },
    getClaimantUpdateOptionsSuccess(state, { payload }: PayloadAction<GetClaimantUpdateOptionsResponse>) {
      state.claimantUpdateOptions = payload;
    },
    getPolicyUpdateOptionsSuccess(state, { payload }: PayloadAction<GetPolicyUpdateOptionsResponse>) {
      state.policyUpdateOptions = payload;
    },
    getProfileUpdateOptionsSuccess(state, { payload }: PayloadAction<GetProfileUpdateOptionsResponse>) {
      state.profileUpdateOptions = payload;
    },
    getDocumentDeliveryPreferencesUpdateOptionsSuccess(state, { payload }: PayloadAction<SelectOption[]>) {
      state.documentDeliveryPreferencesUpdateOptions = payload;
    },
    getGenderUpdateOptionsSuccess(state, { payload }: PayloadAction<SelectOption[]>) {
      state.genderUpdateOptions = payload;
    },
    getBeneficiaryContactPreferenceUpdateOptionsSuccess(
      state,
      { payload }: PayloadAction<ContactPreferenceUpdateOptions>,
    ) {
      state.contactPreferenceUpdateOptions = payload;
    },
    startLoadingStateListData: (state) => {
      state.dataRequestsStates.stateListData = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.stateListData,
        isError: false,
        isSuccess: false,
      };
    },
    getStateListData: () => {},
    getStateListDataSuccess(state, { payload }: PayloadAction<StateSelectOption[]>) {
      state.stateListData = payload;

      if (state.stateListPagination.searchExpression === '' || state.stateListPagination.searchExpression === '-') {
        state.stateListData.unshift({
          value: null,
          label: '-',
          iconCode: null,
        });
      }

      state.dataRequestsStates.stateListData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getStateListDataError(state) {
      state.dataRequestsStates.stateListData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    setStateListSearchExpression(state, { payload }: PayloadAction<string>) {
      state.stateListPagination.searchExpression = payload;
    },
    startLoadingReportingUserListData: (state) => {
      state.dataRequestsStates.reportingUserListData = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.reportingUserListData,
        isError: false,
        isSuccess: false,
      };
    },
    getReportingUserListData: () => {},
    getReportingUserListDataSuccess(
      state,
      { payload }: PayloadAction<PaginatedListNormalizedResponse<UserSelectListItem>>,
    ) {
      if (!state.reportingUserListData || payload.pagination.page === 1) {
        state.reportingUserListData = payload;
      } else {
        state.reportingUserListData.pagination = payload.pagination;
        state.reportingUserListData.dataList = [...state.reportingUserListData.dataList, ...payload.dataList];
      }

      state.dataRequestsStates.reportingUserListData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getReportingUserListDataError(state) {
      state.dataRequestsStates.reportingUserListData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    incrementReportingUserPage(state) {
      state.reportingUserListPagination.page++;
    },
    setReportingUserSearchExpression(state, { payload }: PayloadAction<string>) {
      state.reportingUserListPagination.searchExpression = payload;
      state.reportingUserListPagination.page = 1;
    },

    startLoadingCauseOfIncidentListData: (state) => {
      state.dataRequestsStates.causeOfIncidentListData = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.causeOfIncidentListData,
        isError: false,
        isSuccess: false,
      };
    },
    getCauseOfIncidentListData: () => {},
    getCauseOfIncidentListDataSuccess(state, { payload }: PayloadAction<CauseOfIncidentListItem[]>) {
      state.causeOfIncidentListData = payload;

      state.dataRequestsStates.causeOfIncidentListData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getCauseOfIncidentListDataError(state) {
      state.dataRequestsStates.causeOfIncidentListData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    setCauseOfIncidentSearchExpression(state, { payload }: PayloadAction<string>) {
      state.causeOfIncidentListPagination.searchExpression = payload;
    },
    closeEditMode: (
      _,
      _action: PayloadAction<{
        newState: EditModeState;
        newStatus?: EditModeStatus;
        shouldCloseAllConnections?: boolean;
      }>,
    ) => {},
    enableBlocking: (
      state,
      {
        payload: { except, only },
      }: PayloadAction<{
        except?: { entityName: string; fieldName: string }[];
        only?: { entityName: string; fieldName: string }[];
      }>,
    ) => {
      state.blocking.isEnabled = true;
      state.blocking.except = except;
      state.blocking.only = only;
    },
    disableBlocking: (state) => {
      state.blocking.isEnabled = false;
    },
    getAssigneeById: (state, action: PayloadAction<number>) => {
      const assigneeData = state.assigneeByPersonId[action.payload];
      if (!assigneeData?.status) {
        state.assigneeByPersonId[action.payload] = { status: 'pending' };
      }
    },
    setAssigneesLoading: (state, action: PayloadAction<number[]>) => {
      action.payload.forEach((assigneeId) => {
        const assignee = state.assigneeByPersonId[assigneeId];
        assignee.status = 'loading';
      });
    },
    getAssigneeByIdSuccess: (state, action: PayloadAction<NormalizedV2Response[]>) => {
      action.payload.forEach((assigneeData) => {
        const personId = (assigneeData.entities.Person as NormalizedV2Response).id;
        const assignee = state.assigneeByPersonId[personId];
        assignee.status = 'loaded';
        assignee.data = assigneeData;
      });
    },
    startLoadingAssignmentListData: (state) => {
      state.dataRequestsStates.assignmentListData = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.assignmentListData,
        isError: false,
        isSuccess: false,
      };
    },
    getAssignmentListData: (_state, _action: PayloadAction<{ id?: number } | undefined>) => {},
    getAssignmentListDataSuccess(
      state,
      {
        payload,
      }: PayloadAction<{
        queueId?: number;
        data: AssignmentQueueListItem[] | AssignmentAssigneeListItem[];
      }>,
    ) {
      const { queueId, data } = payload;

      if (queueId != null) {
        state.assigneeListDataByQueueId[String(queueId)] = data as AssignmentAssigneeListItem[];
      } else {
        state.assignmentListData = data as AssignmentQueueListItem[];
      }

      state.dataRequestsStates.assignmentListData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getAssignmentListDataError(state) {
      state.dataRequestsStates.assignmentListData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    setAssignmentListSearchExpression(state, { payload }: PayloadAction<string>) {
      if (state.assignmentListPagination.searchExpression === payload) {
        return;
      }

      state.assignmentListPagination.searchExpression = payload;
      state.assignmentListData = null;
      state.assigneeListDataByQueueId = {};
    },
  },
});

export const {
  resetEditedField,
  removeHiddenEditedFields,
  setEditModeBehaviorOnError,
  changeEditModeStatus,
  getClaimSetUpdateOptionsSuccess,
  getClaimSetClaimUpdateOptionsSuccess,
  getClaimSetTaskUpdateOptionsSuccess,
  getClaimantUpdateOptionsSuccess,
  getBeneficiaryContactPreferenceUpdateOptionsSuccess,
  getClaimSetClaimUpdateOptions,
  getClaimSetTaskUpdateOptions,
  getClaimantUpdateOptions,
  getBeneficiaryContactPreferenceUpdateOptions,
  getDocumentDeliveryPreferencesUpdateOptions,
  getDocumentDeliveryPreferencesUpdateOptionsSuccess,
  getGenderUpdateOptions,
  getGenderUpdateOptionsSuccess,
  getPolicyUpdateOptions,
  getPolicyUpdateOptionsSuccess,
  getProfileUpdateOptions,
  getProfileUpdateOptionsSuccess,
  addEditedField,
  addCustomEditedField,
  changeEditModeState,
  resetEditMode,
  getReportingUserListDataSuccess,
  getCauseOfIncidentListDataSuccess,
  incrementReportingUserPage,
  setReportingUserSearchExpression,
  setCauseOfIncidentSearchExpression,
  getReportingUserListData,
  getReportingUserListDataError,
  getCauseOfIncidentListData,
  getCauseOfIncidentListDataError,
  saveEditModeData,
  getClaimSetUpdateOptions,
  startLoadingReportingUserListData,
  startLoadingStateListData,
  startLoadingCauseOfIncidentListData,
  getStateListData,
  getStateListDataSuccess,
  getStateListDataError,
  setStateListSearchExpression,
  closeEditMode,
  validateFields,
  validateFieldsSuccess,
  validateFieldsError,
  enableBlocking,
  disableBlocking,
  removeEditedField,
  getAssigneeById,
  setAssigneesLoading,
  getAssigneeByIdSuccess,
  startLoadingAssignmentListData,
  getAssignmentListData,
  getAssignmentListDataSuccess,
  getAssignmentListDataError,
  setAssignmentListSearchExpression,
} = editModeSlice.actions;

export const editModeReducer = editModeSlice.reducer;
export const editModeReducerPath = editModeSlice.reducerPath;
